import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import industryData from '../data/industryData.json';
import countriesData from '../data/countriesData.json';
import { useAuth } from '../contexts/KeycloakContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useCredit } from '../contexts/CreditContext';
import ProfileMenu from './ProfileMenu';

const Sidebar = ({
  activeView,
  onViewChange,
  sidebarClosed,
  onToggleSidebar,
  prospectFilterExpanded,
  onToggleProspectFilter,
  filters,
  setFilters,
  userId,
  onSearch,
  searchType,
  currentUser,
  onLogout,
  isAdmin: isAdminProp,
  userCreditStatus = 'available',
}) => {
  const navigate = useNavigate();
  const { keycloak, authenticated } = useAuth();
  const {
    canManageMembers,
    canEditOrgSettings: _unused1,
    isOrganizationWorkspace,
    isOwner: _unused2,
    isSuperadmin: _unused3,
    canBuyCredits,
    workspaceCredits,
    workspaceCreditLimit,
    workspacePlan,
  } = useWorkspace();
  const { isOwner, isOrganizationAdmin, isSuperadmin: isWorkspaceSuperadmin } = useWorkspace();
  const { creditBalance: liveCreditBalance, creditStatus: liveCreditStatus } = useCredit();

  // INLINE ROLE CHECK TO FORCE SUPERADMIN ACCESS
  const getRoles = () => {
    if (!keycloak?.tokenParsed?.realm_access?.roles) return [];
    return keycloak.tokenParsed.realm_access.roles.map((r) => r.toLowerCase());
  };

  const currentRoles = getRoles();
  const userIsAdmin =
    isAdminProp ||
    (authenticated && (currentRoles.includes('admin') || currentRoles.includes('superadmin')));
  const canSeeBilling = isWorkspaceSuperadmin || (isOrganizationWorkspace ? isOwner : true);

  // Direct socket event listener for the most reactive balance updates,
  // bypassing context propagation timing issues.
  const [sidebarLiveBalance, setSidebarLiveBalance] = useState(null);
  useEffect(() => {
    const handle = (e) => {
      if (e.detail?.credit_balance !== undefined) {
        setSidebarLiveBalance(e.detail.credit_balance);
      }
    };
    window.addEventListener('credit-update', handle);
    return () => window.removeEventListener('credit-update', handle);
  }, []);

  const isPoolSharedUser = Boolean(
    isOwner || isOrganizationAdmin || userIsAdmin || isWorkspaceSuperadmin
  );

  const currentTier =
    (isOrganizationWorkspace && isPoolSharedUser ? workspacePlan : currentUser?.tier) || 'Free';
  const showTierAndUpgrade = !userIsAdmin && canSeeBilling && currentTier !== 'Scale';

  // Credit balance:
  // - Admins and Owners share the organization/workspace credit pool (workspaceCredits)
  // - All other team members (Sales Manager, Sales Member, Researcher, Viewer) use their individually assigned balance
  const resolvedBalance =
    sidebarLiveBalance ?? liveCreditBalance ?? currentUser?.creditBalance ?? null;
  const displayCreditBalance =
    isOrganizationWorkspace && isPoolSharedUser
      ? (workspaceCredits ?? resolvedBalance ?? 0)
      : (resolvedBalance ?? 0);

  const FULL_LOGO_SRC = '/image/Logo.png';
  const SIDE_LOGO_SRC = '/image/side_logo.png';

  // Use live credit status from socket/context, fall back to prop
  const effectiveCreditStatus = liveCreditStatus || userCreditStatus;

  const [hoverTimeout, setHoverTimeout] = useState(null);

  const [isLocked, setIsLocked] = useState(() => {
    const saved = localStorage.getItem('sidebarLocked');
    return saved === 'true';
  });

  const [recentRooms, setRecentRooms] = useState([]);
  const [showRecentDropdown, setShowRecentDropdown] = useState(false);
  const [deletingRoomId, setDeletingRoomId] = useState(null);
  const [renamingRoomId, setRenamingRoomId] = useState(null);
  const [renamingValue, setRenamingValue] = useState('');
  const [hoveredRoomId, setHoveredRoomId] = useState(null);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  const [unreadTicketCount, setUnreadTicketCount] = useState(0);

  useEffect(() => {
    if (!userIsAdmin) return;

    const fetchUnreadCount = async () => {
      try {
        const res = await axios.get('/support/unread-count');
        if (res.data.success) {
          setUnreadTicketCount(res.data.data.openCount || 0);
        }
      } catch {
        // silently ignore
      }
    };

    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [userIsAdmin]);

  const activeChatRoomId = window.location.pathname.startsWith('/discover/')
    ? window.location.pathname.split('/')[2]
    : null;

  const fetchRecentRooms = async () => {
    try {
      const res = await axios.get('/chat');
      if (res.data.success) {
        setRecentRooms(res.data.data.slice(0, 10));
      }
    } catch (err) {
      console.error('Failed to fetch rooms for shortcut:', err);
    }
  };

  useEffect(() => {
    fetchRecentRooms();
    const handleRefresh = () => fetchRecentRooms();
    window.addEventListener('refresh-chat-list', handleRefresh);
    return () => window.removeEventListener('refresh-chat-list', handleRefresh);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        (!buttonRef.current || !buttonRef.current.contains(event.target))
      ) {
        setShowRecentDropdown(false);
      }
    };
    if (showRecentDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showRecentDropdown]);

  const deleteChatRoom = async (roomId) => {
    try {
      const res = await axios.delete(`/chat/${roomId}`);
      if (res.data.success) {
        setRecentRooms((prev) => prev.filter((r) => r.id !== roomId));
        if (activeChatRoomId === roomId) {
          window.location.href = '/discover';
        }
      }
    } catch (err) {
      console.error('Failed to delete room:', err);
    }
  };

  const renameChatRoom = async (roomId, newName) => {
    if (!newName || !newName.trim()) {
      setRenamingRoomId(null);
      return;
    }
    try {
      const res = await axios.patch(`/chat/${roomId}`, { name: newName });
      if (res.data.success) {
        setRecentRooms((prev) => prev.map((r) => (r.id === roomId ? { ...r, name: newName } : r)));
      }
    } catch (err) {
      console.error('Failed to rename room:', err);
    } finally {
      setRenamingRoomId(null);
      setRenamingValue('');
    }
  };

  useEffect(() => {
    return () => {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
    };
  }, [hoverTimeout]);

  useEffect(() => {
    localStorage.setItem('sidebarLocked', isLocked);
  }, [isLocked]);

  const PREDEFINED_DEPARTMENTS = [
    'Business Development',
    'Consulting',
    'Customer Service',
    'Engineering & Technical',
    'Finance',
    'General Management',
    'Health Care & Medical',
    'Human Resources',
    'Information Technology',
    'Legal',
    'Marketing',
    'Operations',
    'Other',
    'Product',
    'Research & Analytics',
    'Sales',
  ];

  const SENIORITY_MAPPING = {
    Founder: 10,
    'C-suite': 9,
    'Vice President': 8,
    Partner: 7,
    Director: 6,
    Manager: 5,
    Senior: 4,
    Entry: 3,
    Intern: 2,
    Other: 1,
  };

  const PREDEFINED_SENIORITY = Object.keys(SENIORITY_MAPPING);

  const PREDEFINED_SIZES = [
    '1-10',
    '11-50',
    '51-200',
    '201-500',
    '501-1000',
    '1001-5000',
    '5001-10000',
    '10001-100000',
    '100001+',
  ];

  const PREDEFINED_REVENUES = [
    { min: 1, max: 1000000, label: '$1 - $1M' },
    { min: 1000000, max: 5000000, label: '$1M - $5M' },
    { min: 5000000, max: 10000000, label: '$5M - $10M' },
    { min: 10000000, max: 50000000, label: '$10M - $50M' },
    { min: 50000000, max: 100000000, label: '$50M - $100M' },
    { min: 100000000, max: 250000000, label: '$100M - $250M' },
    { min: 250000000, max: 500000000, label: '$250M - $500M' },
    { min: 500000000, max: 1000000000, label: '$500M - $1B' },
    { min: 1000000000, max: 10000000000, label: '$1B - $10B' },
    { min: 500000000, max: 10000000000, label: '$500M - $10B' },
    { min: 10000000000, max: 100000000000, label: '$10B - $100B' },
    { min: 100000000000, label: '> $100B+' },
  ];

  const [filterOptions, setFilterOptions] = useState({
    departments: PREDEFINED_DEPARTMENTS,
    seniority: PREDEFINED_SENIORITY,
    sizes: PREDEFINED_SIZES,
    industries: [],
    countries: [],
  });

  const [expandedIndustries, setExpandedIndustries] = useState({});
  const [showIndustryTree, setShowIndustryTree] = useState(false);
  const [industrySearch, setIndustrySearch] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [showSizeDropdown, setShowSizeDropdown] = useState(false);
  const [sizeSearch, setSizeSearch] = useState('');
  const [showRevenueDropdown, setShowRevenueDropdown] = useState(false);
  const [revenueSearch, setRevenueSearch] = useState('');
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  useEffect(() => {
    if (activeView !== 'prospect-finder') {
      setCountrySearch('');
      setIndustrySearch('');
      setSizeSearch('');
      setRevenueSearch('');
      setShowCountryDropdown(false);
      setShowIndustryTree(false);
      setShowSizeDropdown(false);
      setShowRevenueDropdown(false);
    }
  }, [activeView]);

  // ── Dropdown components ──────────────────────────────────────────────────

  const SizeDropdown = () => {
    const filteredSizes = PREDEFINED_SIZES.filter((size) => {
      if (!sizeSearch.trim()) return true;
      return size.toLowerCase().includes(sizeSearch.toLowerCase());
    });
    const handleSizeClick = (size) => {
      const currentSizes = filters.companySizes || [];
      if (!currentSizes.includes(size)) {
        setFilters({ ...filters, companySizes: [...currentSizes, size] });
      }
      setSizeSearch('');
    };
    const isSizeSelected = (size) => (filters.companySizes || []).includes(size);
    return (
      <div
        className="size-dropdown"
        onMouseDown={(e) => e.preventDefault()}
        style={{
          maxHeight: '260px',
          overflowY: 'auto',
          overflowX: 'hidden',
          background: '#fff',
          border: '1px solid #ddd',
          borderRadius: '0 0 4px 4px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          padding: '8px 0',
          fontSize: '14px',
          position: 'absolute',
          top: '100%',
          left: -1,
          right: -1,
          zIndex: 1000,
          marginTop: '-1px',
          borderTop: 'none',
        }}
      >
        {filteredSizes.length > 0 ? (
          filteredSizes.map((size, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '5px 8px 4px 8px',
                cursor: 'pointer',
                transition: 'background 0.2s',
              }}
              onMouseOver={(e) => (e.currentTarget.style.background = '#f5f8fa')}
              onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
              onClick={() => handleSizeClick(size)}
              onMouseDown={(e) => e.preventDefault()}
            >
              <input
                type="checkbox"
                className="industry-checkbox"
                checked={isSizeSelected(size)}
                onChange={() => {}}
                onMouseDown={(e) => e.preventDefault()}
              />
              <label style={{ cursor: 'pointer', fontSize: '13px', flex: 1, lineHeight: '1.2' }}>
                {size}
              </label>
            </div>
          ))
        ) : (
          <div
            style={{ padding: '8px 12px', color: '#999', fontSize: '13px', fontStyle: 'italic' }}
          >
            {sizeSearch.trim() ? `We couldn't find "${sizeSearch}"` : 'No sizes found'}
          </div>
        )}
      </div>
    );
  };

  const RevenueDropdown = () => {
    const filteredRevenues = PREDEFINED_REVENUES.filter((revenue) => {
      if (!revenueSearch.trim()) return true;
      return revenue.label.toLowerCase().includes(revenueSearch.toLowerCase());
    });
    const handleRevenueClick = (revenue) => {
      const currentRevenues = filters.companyRevenues || [];
      const revenueExists = currentRevenues.some(
        (r) => r.min === revenue.min && r.max === revenue.max
      );
      if (!revenueExists) {
        const newRevenue = { min: revenue.min };
        if (revenue.max) newRevenue.max = revenue.max;
        setFilters({ ...filters, companyRevenues: [...currentRevenues, newRevenue] });
      }
      setRevenueSearch('');
    };
    const isRevenueSelected = (revenue) =>
      (filters.companyRevenues || []).some((r) => r.min === revenue.min && r.max === revenue.max);
    return (
      <div
        className="revenue-dropdown"
        onMouseDown={(e) => e.preventDefault()}
        style={{
          maxHeight: '260px',
          overflowY: 'auto',
          overflowX: 'hidden',
          background: '#fff',
          border: '1px solid #ddd',
          borderRadius: '0 0 4px 4px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          padding: '8px 0',
          fontSize: '14px',
          position: 'absolute',
          top: '100%',
          left: -1,
          right: -1,
          zIndex: 1000,
          marginTop: '-1px',
          borderTop: 'none',
        }}
      >
        {filteredRevenues.length > 0 ? (
          filteredRevenues.map((revenue, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '5px 8px 4px 8px',
                cursor: 'pointer',
                transition: 'background 0.2s',
              }}
              onMouseOver={(e) => (e.currentTarget.style.background = '#f5f8fa')}
              onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
              onClick={() => handleRevenueClick(revenue)}
              onMouseDown={(e) => e.preventDefault()}
            >
              <input
                type="checkbox"
                className="industry-checkbox"
                checked={isRevenueSelected(revenue)}
                onChange={() => {}}
                onMouseDown={(e) => e.preventDefault()}
              />
              <label style={{ cursor: 'pointer', fontSize: '13px', flex: 1, lineHeight: '1.2' }}>
                {revenue.label}
              </label>
            </div>
          ))
        ) : (
          <div
            style={{ padding: '8px 12px', color: '#999', fontSize: '13px', fontStyle: 'italic' }}
          >
            {revenueSearch.trim() ? `We couldn't find "${revenueSearch}"` : 'No revenues found'}
          </div>
        )}
      </div>
    );
  };

  const CountryDropdown = () => {
    const filteredCountries = countriesData.filter((country) => {
      if (!countrySearch.trim()) return true;
      return country.name.toLowerCase().includes(countrySearch.toLowerCase());
    });
    const handleCountryClick = (countryName) => {
      const currentLocations = filters.locations || [];
      if (!currentLocations.includes(countryName)) {
        setFilters({ ...filters, locations: [...currentLocations, countryName] });
      }
      setCountrySearch('');
    };
    const isCountrySelected = (countryName) => (filters.locations || []).includes(countryName);
    return (
      <div
        className="country-dropdown"
        onMouseDown={(e) => e.preventDefault()}
        style={{
          maxHeight: '260px',
          overflowY: 'auto',
          overflowX: 'hidden',
          background: '#fff',
          border: '1px solid #ddd',
          borderRadius: '0 0 4px 4px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          padding: '8px 0',
          fontSize: '14px',
          position: 'absolute',
          top: '100%',
          left: -1,
          right: -1,
          zIndex: 1000,
          marginTop: '-1px',
          borderTop: 'none',
        }}
      >
        {filteredCountries.length > 0 ? (
          filteredCountries.map((country, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '5px 8px 4px 8px',
                cursor: 'pointer',
                transition: 'background 0.2s',
              }}
              onMouseOver={(e) => (e.currentTarget.style.background = '#f5f8fa')}
              onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
              onClick={() => handleCountryClick(country.name)}
              onMouseDown={(e) => e.preventDefault()}
            >
              <input
                type="checkbox"
                className="industry-checkbox"
                checked={isCountrySelected(country.name)}
                onChange={() => {}}
                onMouseDown={(e) => e.preventDefault()}
              />
              <label style={{ cursor: 'pointer', fontSize: '13px', flex: 1, lineHeight: '1.2' }}>
                {country.name}
                <span style={{ marginLeft: '8px', color: '#999', fontSize: '12px' }}>
                  ({country.code})
                </span>
              </label>
            </div>
          ))
        ) : (
          <div
            style={{ padding: '8px 12px', color: '#999', fontSize: '13px', fontStyle: 'italic' }}
          >
            {countrySearch.trim() ? `We couldn't find "${countrySearch}"` : 'No countries found'}
          </div>
        )}
      </div>
    );
  };

  const IndustryTree = () => {
    const filteredData = industryData
      .map((item) => {
        if (!industrySearch.trim()) return item;
        const searchLower = industrySearch.toLowerCase();
        const mainMatch = item.main_industry.toLowerCase().includes(searchLower);
        const matchingSubs = (item.sub_industries || []).filter((sub) =>
          sub.value.toLowerCase().includes(searchLower)
        );
        if (mainMatch) return item;
        else if (matchingSubs.length > 0)
          return { ...item, sub_industries: matchingSubs, forceExpand: true };
        return null;
      })
      .filter(Boolean);

    const toggleMainIndustry = (mainIndustryName) => {
      setExpandedIndustries((prev) => ({ ...prev, [mainIndustryName]: !prev[mainIndustryName] }));
    };
    const handleMainIndustryCheck = (mainIndustryName, checked) => {
      const currentIndustries = filters.industries || [];
      const mainIndustryLabel = `${mainIndustryName} (Main)`;
      if (checked) {
        if (!currentIndustries.includes(mainIndustryLabel)) {
          setFilters({ ...filters, industries: [...currentIndustries, mainIndustryLabel] });
        }
      } else {
        setFilters({
          ...filters,
          industries: currentIndustries.filter((ind) => ind !== mainIndustryLabel),
        });
      }
    };
    const handleSubIndustryCheck = (subIndustryName, checked) => {
      const currentIndustries = filters.industries || [];
      const subIndustryLabel = `${subIndustryName} (Sub)`;
      if (checked) {
        if (!currentIndustries.includes(subIndustryLabel)) {
          setFilters({ ...filters, industries: [...currentIndustries, subIndustryLabel] });
        }
      } else {
        setFilters({
          ...filters,
          industries: currentIndustries.filter((ind) => ind !== subIndustryLabel),
        });
      }
    };
    const isMainIndustryChecked = (mainIndustryName) =>
      (filters.industries || []).includes(`${mainIndustryName} (Main)`);
    const isSubIndustryChecked = (subIndustryName) =>
      (filters.industries || []).includes(`${subIndustryName} (Sub)`);

    return (
      <div
        className="industry-tree"
        onMouseDown={(e) => e.preventDefault()}
        style={{
          maxHeight: '260px',
          overflowY: 'auto',
          background: '#fff',
          border: '1px solid #ddd',
          borderRadius: '0 0 4px 4px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          padding: '8px 0',
          fontSize: '14px',
          position: 'absolute',
          top: '100%',
          left: -1,
          right: -1,
          zIndex: 1000,
          marginTop: '-1px',
          borderTop: 'none',
        }}
      >
        {filteredData.length > 0 ? (
          filteredData.map((industry, index) => {
            const isExpanded = industry.forceExpand || expandedIndustries[industry.main_industry];
            return (
              <div
                key={index}
                style={{ marginBottom: '0', transition: 'background 0.2s' }}
                onMouseOver={(e) => (e.currentTarget.style.background = '#f5f8fa')}
                onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <div style={{ display: 'flex', alignItems: 'center', padding: '4px 8px' }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleMainIndustry(industry.main_industry);
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '0',
                      fontSize: '10px',
                      width: '0px',
                      height: '16px',
                      display: 'flex',
                      alignItems: 'left',
                      justifyContent: 'flex-start',
                      color: '#888',
                      transition: 'background 0.2s',
                      flexShrink: 0,
                      marginRight: '20px',
                      borderRadius: '2px',
                    }}
                    title={isExpanded ? 'Collapse' : 'Expand'}
                  >
                    <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`}></i>
                  </button>
                  <input
                    type="checkbox"
                    className="industry-checkbox"
                    checked={isMainIndustryChecked(industry.main_industry)}
                    onChange={(e) =>
                      handleMainIndustryCheck(industry.main_industry, e.target.checked)
                    }
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.preventDefault()}
                  />
                  <label
                    style={{
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: 400,
                      flex: 1,
                      lineHeight: '1.2',
                    }}
                    onClick={() => toggleMainIndustry(industry.main_industry)}
                  >
                    {industry.main_industry}
                  </label>
                </div>
                {isExpanded && industry.sub_industries && (
                  <div style={{ paddingLeft: '0', marginTop: '0' }}>
                    {industry.sub_industries.map((subInd, subIndex) => (
                      <div
                        key={subIndex}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '5px 8px 4px 40px',
                        }}
                        onMouseOver={(e) => (e.currentTarget.style.background = '#f5f8fa')}
                        onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        <input
                          type="checkbox"
                          className="industry-checkbox"
                          checked={
                            isMainIndustryChecked(industry.main_industry) ||
                            isSubIndustryChecked(subInd.value)
                          }
                          onChange={(e) => handleSubIndustryCheck(subInd.value, e.target.checked)}
                          onMouseDown={(e) => e.preventDefault()}
                        />
                        <label
                          style={{
                            cursor: 'pointer',
                            fontSize: '13px',
                            flex: 1,
                            lineHeight: '1.2',
                          }}
                        >
                          {subInd.value}
                        </label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <div
            style={{ padding: '8px 12px', color: '#999', fontSize: '13px', fontStyle: 'italic' }}
          >
            No industries found
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    try {
      const allIndustries = [];
      industryData.forEach((item) => {
        allIndustries.push(`${item.main_industry} (Main)`);
        if (item.sub_industries && Array.isArray(item.sub_industries)) {
          item.sub_industries.forEach((subInd) => {
            if (subInd.value) allIndustries.push(`${subInd.value} (Sub)`);
          });
        }
      });
      setFilterOptions((prev) => ({ ...prev, industries: allIndustries }));
    } catch (error) {
      console.error('Error loading industries:', error);
    }
  }, []);

  const handleSearch = () => {
    navigate('/prospects');
    if (onSearch) onSearch(filters);
  };

  const handleClearFilters = () => {
    setFilters({
      jobTitles: [],
      managementLevels: [],
      departments: [],
      locations: [],
      companyNames: [],
      industries: [],
      companySizes: [],
      companyRevenues: [],
      skills: [],
    });
    setCountrySearch('');
    setIndustrySearch('');
    setSizeSearch('');
    setRevenueSearch('');
    setShowCountryDropdown(false);
    setShowIndustryTree(false);
    setShowSizeDropdown(false);
    setShowRevenueDropdown(false);
  };

  const addChip = (filterKey, value) => {
    if (!value.trim()) return;
    const currentValues = filters[filterKey] || [];
    if (!currentValues.includes(value.trim())) {
      setFilters({ ...filters, [filterKey]: [...currentValues, value.trim()] });
    }
  };

  const removeChip = (filterKey, value) => {
    setFilters({ ...filters, [filterKey]: filters[filterKey].filter((item) => item !== value) });
  };

  const ChipInput = ({ filterKey, placeholder, datalistOptions = null, onFocus = null }) => {
    const [inputValue, setInputValue] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [filteredSuggestions, setFilteredSuggestions] = useState([]);

    useEffect(() => {
      if (activeView !== 'prospect-finder') {
        setInputValue('');
        setShowSuggestions(false);
        setFilteredSuggestions([]);
      }
    }, [activeView]);

    useEffect(() => {
      if (datalistOptions && datalistOptions.length > 0) {
        if (inputValue.trim()) {
          setFilteredSuggestions(
            datalistOptions.filter((option) =>
              option.toLowerCase().includes(inputValue.toLowerCase())
            )
          );
        } else {
          setFilteredSuggestions(datalistOptions);
        }
      } else {
        setFilteredSuggestions([]);
      }
    }, [inputValue, datalistOptions]);

    const handleKeyDown = (e) => {
      if (e.key === 'Enter' || e.key === ',') {
        e.preventDefault();
        if (datalistOptions && datalistOptions.length > 0) {
          if (filteredSuggestions.length > 0) {
            addChip(filterKey, filteredSuggestions[0]);
            setInputValue('');
          } else {
            setInputValue('');
          }
        } else {
          if (inputValue.trim()) {
            addChip(filterKey, inputValue.replace(',', ''));
            setInputValue('');
          }
        }
        setShowSuggestions(false);
      }
    };

    const handleBlur = () => {
      setTimeout(() => {
        if (datalistOptions && datalistOptions.length > 0) {
          if (inputValue.trim() && filteredSuggestions.length > 0) {
            addChip(filterKey, filteredSuggestions[0]);
            setInputValue('');
          } else if (inputValue.trim()) {
            setInputValue('');
          }
        } else {
          if (inputValue.trim()) {
            addChip(filterKey, inputValue);
            setInputValue('');
          }
        }
        setShowSuggestions(false);
      }, 200);
    };

    const handleFocus = () => {
      if (onFocus) onFocus();
      if (datalistOptions && datalistOptions.length > 0) setShowSuggestions(true);
    };

    const handleSuggestionClick = (suggestion) => {
      addChip(filterKey, suggestion);
      setInputValue('');
      setShowSuggestions(false);
    };

    return (
      <div className="chip-container" style={{ position: 'relative' }}>
        {(filters[filterKey] || []).map((chip, index) => {
          const isIndustry = filterKey === 'industries';
          const isMain = isIndustry && chip.includes('(Main)');
          const isSub = isIndustry && chip.includes('(Sub)');
          const chipClass = isMain ? 'chip industry-main' : isSub ? 'chip industry-sub' : 'chip';
          const displayText = chip.replace(/\s*\((Main|Sub)\)/, '');
          return (
            <div key={index} className={chipClass}>
              {displayText}
              <span className="chip-delete" onClick={() => removeChip(filterKey, chip)}>
                <i className="fas fa-times"></i>
              </span>
            </div>
          );
        })}
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onFocus={handleFocus}
          placeholder={placeholder}
        />
        {showSuggestions && (
          <div
            className="autocomplete-suggestions"
            style={{ maxHeight: '200px', overflowY: 'auto' }}
          >
            {filteredSuggestions.length > 0
              ? filteredSuggestions.map((suggestion, index) => {
                  const hasLabel = suggestion.includes('(Main)') || suggestion.includes('(Sub)');
                  const mainText = hasLabel
                    ? suggestion.replace(/\s*\((Main|Sub)\)/, '')
                    : suggestion;
                  const labelText = suggestion.includes('(Main)')
                    ? 'Main'
                    : suggestion.includes('(Sub)')
                      ? 'Sub'
                      : null;
                  const labelClass = labelText ? labelText.toLowerCase() : '';
                  return (
                    <div
                      key={index}
                      className="suggestion-item"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleSuggestionClick(suggestion)}
                    >
                      <span>{mainText}</span>
                      {labelText && (
                        <span className={`suggestion-label ${labelClass}`}>{labelText}</span>
                      )}
                    </div>
                  );
                })
              : datalistOptions &&
                datalistOptions.length > 0 &&
                inputValue.trim() && (
                  <div
                    className="suggestion-item no-results"
                    style={{ cursor: 'default', color: '#999', fontStyle: 'italic' }}
                  >
                    We couldn't find "{inputValue}"
                  </div>
                )}
          </div>
        )}
      </div>
    );
  };

  const handleMouseOver = (e) => {
    if (isLocked) return;
    if (e.target.closest('.sidebar-locker-btn')) {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
        setHoverTimeout(null);
      }
      return;
    }
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    if (sidebarClosed) onToggleSidebar();
  };

  const handleMouseOut = (e) => {
    if (isLocked) return;
    const sidebar = e.currentTarget;
    const relatedTarget = e.relatedTarget;
    if (sidebar.contains(relatedTarget)) return;
    const timeout = setTimeout(() => {
      if (!sidebarClosed) onToggleSidebar();
    }, 600);
    setHoverTimeout(timeout);
  };

  const handleToggleLock = () => {
    setIsLocked(!isLocked);
  };

  // ── Main render ──────────────────────────────────────────────────────────

  return (
    <div
      className={`sidebar ${sidebarClosed ? 'closed' : ''}`}
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
    >
      <button
        className="sidebar-locker-btn"
        onClick={(e) => {
          e.stopPropagation();
          handleToggleLock();
        }}
        title={
          isLocked
            ? 'Unlock Sidebar (Enable hover behavior)'
            : 'Lock Sidebar (Disable hover behavior)'
        }
        style={{
          position: 'absolute',
          right: '-14px',
          top: '25px',
          width: '28px',
          height: '28px',
          borderRadius: '50%',
          backgroundColor: '#fff',
          border: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          zIndex: 9999,
          color: isLocked ? '#2563eb' : '#64748b',
          transition: 'all 0.2s',
          padding: 0,
        }}
      >
        <i className={`fas fa-${isLocked ? 'lock' : 'lock-open'}`}></i>
      </button>

      <div className="sidebar-inner">
        <div
          className="sidebar-header"
          style={{ position: 'relative', width: '100%', marginBottom: '10px', flexShrink: 0 }}
        >
          <div
            className="logo"
            onClick={() => navigate(userIsAdmin ? '/admin/user-management' : '/discover')}
            style={{ cursor: 'pointer' }}
          >
            <img
              src={sidebarClosed ? SIDE_LOGO_SRC : FULL_LOGO_SRC}
              alt="Company Logo"
              style={{ padding: sidebarClosed ? '0 10px' : '0' }}
            />
          </div>
        </div>

        <div className="sidebar-main-nav">
          {/* FEATURES SECTION */}
          {!userIsAdmin && (
            <>
              {!sidebarClosed && <div className="menu-header">DISCOVERY</div>}

              <div
                className={`menu-item ${activeView === 'discover' && !activeChatRoomId ? 'active' : ''}`}
                onClick={() => {
                  if (
                    window.location.pathname === '/discover' ||
                    window.location.pathname === '/'
                  ) {
                    window.location.href = '/discover';
                  } else {
                    navigate('/discover');
                  }
                }}
                style={{ fontSize: '14px', fontWeight: 600 }}
              >
                <span className="icon">
                  <i
                    className="fas fa-wand-magic-sparkles"
                    style={{
                      color: activeView === 'discover' && !activeChatRoomId ? '#2563eb' : 'inherit',
                    }}
                  ></i>
                </span>
                <span
                  style={{
                    color: activeView === 'discover' && !activeChatRoomId ? '#2563eb' : 'inherit',
                  }}
                >
                  Discovery
                </span>
              </div>

              {!sidebarClosed && (
                <div className="menu-header" style={{ marginTop: '5px' }}>
                  CHAT
                </div>
              )}

              <div className="sidebar-chat-list">
                {recentRooms.length > 0 ? (
                  recentRooms.map((room) => (
                    <div
                      key={room.id}
                      className={`menu-item chat-room-item ${activeChatRoomId === room.id ? 'active' : ''}`}
                      onClick={() => {
                        if (renamingRoomId === room.id) return;
                        navigate(`/discover/${room.id}`);
                      }}
                      style={{
                        paddingLeft: '24px',
                        fontSize: '13px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        position: 'relative',
                      }}
                      onMouseEnter={() => setHoveredRoomId(room.id)}
                      onMouseLeave={() => setHoveredRoomId(null)}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          overflow: 'hidden',
                          flex: 1,
                        }}
                      >
                        <span
                          className="icon"
                          style={{
                            width: '16px',
                            minWidth: '16px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            lineHeight: 1,
                            opacity: 0.8,
                            marginRight: '8px',
                          }}
                        >
                          <i
                            className={`fa-solid ${activeChatRoomId === room.id ? 'fa-comment-dots' : 'fa-comment'}`}
                          ></i>
                        </span>
                        {renamingRoomId === room.id ? (
                          <input
                            className="rename-input-sidebar"
                            autoFocus
                            value={renamingValue}
                            onChange={(e) => setRenamingValue(e.target.value)}
                            onBlur={() => renameChatRoom(room.id, renamingValue)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') renameChatRoom(room.id, renamingValue);
                              if (e.key === 'Escape') setRenamingRoomId(null);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              width: '100%',
                              fontSize: '12px',
                              border: '1px solid #2563eb',
                              borderRadius: '3px',
                              padding: '2px',
                            }}
                          />
                        ) : (
                          <span
                            className="room-name-sidebar"
                            style={{
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: sidebarClosed ? '0' : '120px',
                            }}
                          >
                            {room.name}
                          </span>
                        )}
                      </div>
                      {!sidebarClosed && (
                        <div
                          className="chat-actions-container"
                          style={{
                            display: 'flex',
                            minWidth: '60px',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            height: '24px',
                          }}
                        >
                          {deletingRoomId === room.id ? (
                            <div
                              className="delete-confirm-sidebar"
                              style={{ display: 'flex', gap: '6px', alignItems: 'center' }}
                            >
                              <button
                                className="confirm-icon-mini-btn yes"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteChatRoom(room.id);
                                }}
                                title="Confirm Delete"
                                style={{
                                  background: '#fee2e2',
                                  border: 'none',
                                  borderRadius: '4px',
                                  width: '22px',
                                  height: '22px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                }}
                              >
                                <i
                                  className="fas fa-check"
                                  style={{ color: '#ef4444', fontSize: '10px' }}
                                ></i>
                              </button>
                              <button
                                className="confirm-icon-mini-btn no"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeletingRoomId(null);
                                }}
                                title="Cancel"
                                style={{
                                  background: '#f1f5f9',
                                  border: 'none',
                                  borderRadius: '4px',
                                  width: '22px',
                                  height: '22px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                }}
                              >
                                <i
                                  className="fas fa-times"
                                  style={{ color: '#64748b', fontSize: '10px' }}
                                ></i>
                              </button>
                            </div>
                          ) : (
                            <div
                              className="sidebar-chat-actions"
                              style={{
                                display: 'flex',
                                opacity:
                                  hoveredRoomId === room.id && renamingRoomId !== room.id ? 0.8 : 0,
                                pointerEvents:
                                  hoveredRoomId === room.id && renamingRoomId !== room.id
                                    ? 'auto'
                                    : 'none',
                                transition: 'opacity 0.2s ease',
                              }}
                            >
                              <button
                                className="chat-action-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setRenamingRoomId(room.id);
                                  setRenamingValue(room.name);
                                }}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  padding: '4px',
                                  cursor: 'pointer',
                                }}
                              >
                                <i className="fas fa-pen" style={{ fontSize: '10px' }}></i>
                              </button>
                              <button
                                className="chat-action-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeletingRoomId(room.id);
                                }}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  padding: '4px',
                                  cursor: 'pointer',
                                }}
                              >
                                <i className="fas fa-trash-can" style={{ fontSize: '10px' }}></i>
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div
                    className="no-chats-sidebar"
                    style={{
                      padding: '0 24px',
                      fontSize: '12px',
                      opacity: 0.5,
                      fontStyle: 'italic',
                      marginBottom: '10px',
                    }}
                  >
                    No recent chats
                  </div>
                )}
              </div>

              {!sidebarClosed && <div className="menu-header">HISTORY</div>}

              <div
                className={`menu-item ${activeView === 'prospect-list' ? 'active' : ''}`}
                onClick={() => {
                  if (userIsAdmin) return;
                  navigate('/prospect-list');
                }}
                style={{ fontSize: '14px', fontWeight: 500 }}
              >
                <span className="icon">
                  <i className="fas fa-list-ul"></i>
                </span>
                <span>Prospect List</span>
              </div>

              <div
                className={`menu-item ${activeView === 'analysis-reports' ? 'active' : ''}`}
                onClick={() => {
                  navigate('/analysis-reports');
                }}
                style={{ fontSize: '14px', fontWeight: 500 }}
              >
                <span className="icon">
                  <i className="fas fa-file-pdf"></i>
                </span>
                <span>Analysis Report</span>
              </div>

              <div
                className={`menu-item ${activeView === 'customer-management' ? 'active' : ''}`}
                onClick={() => navigate('/customer-management')}
                style={{ fontSize: '14px', fontWeight: 500 }}
              >
                <span className="icon">
                  <i className="fa-solid fa-users-gear"></i>
                </span>
                <span>Lead Pipeline</span>
              </div>

              <div
                className={`menu-item ${activeView === 'meetings' ? 'active' : ''}`}
                onClick={() => navigate('/meetings')}
                style={{ fontSize: '14px', fontWeight: 500 }}
              >
                <span className="icon">
                  <i className="fas fa-calendar-check"></i>
                </span>
                <span>Meetings</span>
              </div>

              <div
                className={`menu-item ${activeView === 'campaign' ? 'active' : ''}`}
                onClick={() => navigate('/campaign')}
                style={{ fontSize: '14px', fontWeight: 500 }}
              >
                <span className="icon">
                  <i className="fas fa-bullhorn"></i>
                </span>
                <span>Campaign</span>
              </div>

              <div
                className={`menu-item ${activeView === 'email-outreach' ? 'active' : ''}`}
                onClick={() => navigate('/email-outreach')}
                style={{ fontSize: '14px', fontWeight: 500 }}
              >
                <span className="icon">
                  <i className="fas fa-envelope-open-text"></i>
                </span>
                <span>Email Outreach</span>
              </div>

              <div
                className={`menu-item ${activeView === 'seo-aieo' ? 'active' : ''}`}
                onClick={() => navigate('/seo-aieo')}
                style={{ fontSize: '14px', fontWeight: 500 }}
              >
                <span className="icon">
                  <i className="fas fa-globe"></i>
                </span>
                <span>SEO &amp; AIEO</span>
              </div>

              <div
                className={`menu-item ${activeView === 'landing-pages' ? 'active' : ''}`}
                onClick={() => navigate('/landing-pages')}
                style={{ fontSize: '14px', fontWeight: 500 }}
              >
                <span className="icon">
                  <i className="fas fa-pager"></i>
                </span>
                <span>Landing Pages</span>
              </div>

              <div
                className={`menu-item ${activeView === 'knowledge' ? 'active' : ''}`}
                onClick={() => navigate('/knowledge')}
                style={{ fontSize: '14px', fontWeight: 500 }}
              >
                <span className="icon">
                  <i className="fa-solid fa-book-bookmark"></i>
                </span>
                <span>Knowledge</span>
              </div>

              <div
                className={`menu-item ${activeView === 'analytics' ? 'active' : ''}`}
                onClick={() => navigate('/analytics')}
                style={{ fontSize: '14px', fontWeight: 500 }}
              >
                <span className="icon">
                  <i className="fa-solid fa-chart-line"></i>
                </span>
                <span>Analytics and Attribution</span>
              </div>

              {!(isOrganizationWorkspace && (isOwner || isOrganizationAdmin)) && (
                <div
                  className={`menu-item ${activeView === 'personal-credits' ? 'active' : ''}`}
                  onClick={() => navigate('/credits')}
                  style={{ fontSize: '14px', fontWeight: 500 }}
                >
                  <span className="icon">
                    <i className="fas fa-coins"></i>
                  </span>
                  <span>Credit Usage</span>
                </div>
              )}
            </>
          )}

          {/* WORKSPACE SECTION */}
          {isOrganizationWorkspace && canManageMembers && (
            <>
              {!sidebarClosed && <div className="menu-header">WORKSPACE</div>}
              <div
                className={`menu-item ${activeView === 'members' || activeView === 'activity-logs' ? 'active' : ''}`}
                onClick={() => navigate('/members')}
                style={{ fontSize: '14px', fontWeight: 500 }}
              >
                <span className="icon">
                  <i className="fas fa-users"></i>
                </span>
                <span>Team Management</span>
              </div>
              {canBuyCredits && (
                <div
                  className={`menu-item ${activeView === 'workspace-credits' ? 'active' : ''}`}
                  onClick={() => navigate('/workspace/credits')}
                  style={{ fontSize: '14px', fontWeight: 500, position: 'relative' }}
                >
                  <span className="icon" style={{ position: 'relative' }}>
                    <i className="fas fa-coins"></i>
                    <span className={`icon-status-overlay ${effectiveCreditStatus}`}></span>
                  </span>
                  <span>Credit Management</span>
                </div>
              )}
            </>
          )}

          {/* ADMIN SECTION */}
          {userIsAdmin && (
            <>
              {!sidebarClosed && <div className="menu-header">ADMIN</div>}
              <div
                className={`menu-item ${activeView === 'user-management' || activeView === 'audit-logs' ? 'active' : ''}`}
                onClick={() => navigate('/admin/user-management')}
                style={{ fontSize: '14px', fontWeight: 500 }}
              >
                <span className="icon">
                  <i className="fas fa-users"></i>
                </span>
                <span>User Management</span>
              </div>
              <div
                className={`menu-item ${activeView === 'admin-credits' || activeView === 'admin/credits' ? 'active' : ''}`}
                onClick={() => navigate('/admin/credits')}
                style={{ fontSize: '14px', fontWeight: 500, position: 'relative' }}
              >
                <span className="icon" style={{ position: 'relative' }}>
                  <i className="fas fa-coins"></i>
                  <span className={`icon-status-overlay ${effectiveCreditStatus}`}></span>
                </span>
                <span>Credit Management</span>
              </div>
              <div
                className={`menu-item ${activeView === 'admin-support' ? 'active' : ''}`}
                onClick={() => navigate('/admin/support')}
                style={{ fontSize: '14px', fontWeight: 500 }}
              >
                <span className="icon" style={{ position: 'relative' }}>
                  <i className="fas fa-headset"></i>
                  {unreadTicketCount > 0 && (
                    <span
                      style={{
                        position: 'absolute',
                        top: '-6px',
                        right: '-8px',
                        background: '#ef4444',
                        color: '#fff',
                        fontSize: '9px',
                        fontWeight: '800',
                        borderRadius: '10px',
                        padding: '1px 5px',
                        minWidth: '16px',
                        textAlign: 'center',
                        lineHeight: '14px',
                        boxShadow: '0 1px 4px rgba(239,68,68,0.5)',
                        animation: 'badgePulse 2s infinite',
                      }}
                    >
                      {unreadTicketCount > 99 ? '99+' : unreadTicketCount}
                    </span>
                  )}
                </span>
                <span>Support Management</span>
              </div>
              <div
                className={`menu-item ${activeView === 'policy-checkpoint' ? 'active' : ''}`}
                onClick={() => navigate('/admin/policy-checkpoint')}
                style={{ fontSize: '14px', fontWeight: 500 }}
              >
                <span className="icon">
                  <i className="fas fa-shield-alt" style={{ color: '#3b82f6' }}></i>
                </span>
                <span>Policy Checkpoint</span>
              </div>
            </>
          )}
        </div>

        {/* Sticky user info at bottom when sidebar open */}
        {!sidebarClosed && currentUser && (
          <div
            className="user-info"
            onClick={() => setShowProfileMenu(true)}
            style={{
              width: '100%',
              borderTop: '1px solid #e0e0e0',
              background: '#fff',
              padding: '12px 16px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              flexShrink: 0,
              marginTop: 'auto',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#eff6ff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#fff';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <i
                  className="fas fa-user-circle"
                  style={{ fontSize: '24px', color: '#2563eb' }}
                ></i>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span
                      style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#0f172a',
                        textTransform: 'uppercase',
                      }}
                    >
                      {currentUser.username}
                    </span>
                    {showTierAndUpgrade && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate('/subscription');
                        }}
                        style={{
                          padding: '2px 8px',
                          fontSize: '10px',
                          borderRadius: '4px',
                          background: '#2563eb',
                          color: 'white',
                          border: 'none',
                          cursor: 'pointer',
                          fontWeight: '600',
                          transition: 'all 0.2s',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = '#5a6fd1')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = '#2563eb')}
                      >
                        Update Tier
                      </button>
                    )}
                  </div>
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}
                  >
                    {!userIsAdmin && (
                      <>
                        <span
                          style={{
                            fontSize: '11px',
                            fontWeight: '800',
                            color: '#64748b',
                            textTransform: 'uppercase',
                            letterSpacing: '0.02em',
                          }}
                        >
                          {currentTier.toUpperCase()}
                        </span>
                        <span style={{ fontSize: '11px', color: '#94a3b8' }}>•</span>
                        <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '500' }}>
                          {displayCreditBalance.toLocaleString()} Credits
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <i
                className="fas fa-chevron-up"
                style={{ fontSize: '14px', color: '#94a3b8', flexShrink: 0 }}
              ></i>
            </div>
          </div>
        )}

        {/* Profile icon for closed sidebar */}
        {sidebarClosed && currentUser && (
          <button
            onClick={() => setShowProfileMenu(true)}
            style={{
              width: '100%',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '12px 0',
              color: '#2563eb',
              fontSize: '22px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderTop: '1px solid #e0e0e0',
              backgroundColor: '#fff',
              zIndex: 10,
              transition: 'all 0.2s ease',
              flexShrink: 0,
              marginTop: 'auto',
            }}
            title="Profile"
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#eff6ff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#fff';
            }}
          >
            <i className="fas fa-user-circle"></i>
          </button>
        )}

        {showProfileMenu && (
          <ProfileMenu
            currentUser={currentUser}
            showTierAndUpgrade={showTierAndUpgrade}
            onClose={() => setShowProfileMenu(false)}
            onLogout={() => {
              setShowProfileMenu(false);
              onLogout();
            }}
          />
        )}
      </div>
    </div>
  );
};

export default Sidebar;
